import {
  add, addDays, isAfter, isBefore, subDays,
} from 'date-fns'

import constants from '@/constants'
import api from '@/logic/api'
import DateManager from '@/logic/DateManager'

export const calculateParticipationEnd = (course, participation) => {
  if (!course || !participation) {
    return null
  }
  const startedAt = DateManager.parseModelDate(participation.created_at)
  const duration = {}
  switch (course.participation_duration_type) {
    case constants.COURSES.PARTICIPATION_DURATION_TYPE_DAYS:
      duration.days = course.participation_duration
      break
    case constants.COURSES.PARTICIPATION_DURATION_TYPE_WEEKS:
      duration.weeks = course.participation_duration
      break
    case constants.COURSES.PARTICIPATION_DURATION_TYPE_MONTHS:
      duration.months = course.participation_duration
      break
    default:
      break
  }
  return add(startedAt, duration)
}

export const isCourseExpired = (course, now = DateManager.now()) => {
  if (course.archived_at) {
    return false
  }
  if ([true, false].includes(course.participation?.passed)) {
    return false
  }
  if (course.duration_type === constants.COURSES.DURATION_TYPE_FIXED && course.available_until && isBefore(DateManager.parseModelDate(course.available_until), now)) {
    return true
  }
  if (course.duration_type === constants.COURSES.DURATION_TYPE_DYNAMIC) {
    const participationEndDate = course.participation?.expires_at
      ? DateManager.parseModelDate(course.participation.expires_at)
      : calculateParticipationEnd(course, course.participation)
    if (participationEndDate && isBefore(participationEndDate, now)) {
      return true
    }
  }
  return false
}

export const filterPendingCourses = (courses, now = DateManager.now()) => {
  const sevenDaysFromNow = addDays(now, 7)

  return (courses || []).filter((course) => {
    if (course.participation && isCourseExpired(course, now)) {
      return true
    }

    if (course.is_mandatory) {
      if (course.duration_type === constants.COURSES.DURATION_TYPE_FIXED) {
        return !course.available_until || isBefore(now, DateManager.parseModelDate(course.available_until))
      }
      return true
    }

    if (course.duration_type === constants.COURSES.DURATION_TYPE_FIXED) {
      if (!course.available_until) {
        return false
      }
      const availableUntilDate = DateManager.parseModelDate(course.available_until)
      return isAfter(sevenDaysFromNow, availableUntilDate) && isBefore(now, availableUntilDate)
    }

    if (course.duration_type === constants.COURSES.DURATION_TYPE_DYNAMIC && course.participation) {
      const expiryDate = course.participation.expires_at
        ? DateManager.parseModelDate(course.participation.expires_at)
        : calculateParticipationEnd(course, course.participation)
      return expiryDate && isAfter(sevenDaysFromNow, expiryDate) && isBefore(now, expiryDate)
    }

    return false
  })
}

const HOME_PENDING_COURSE_WINDOW_DAYS = 14

function nullableTimestamp(date) {
  const value = date?.getTime()
  return Number.isFinite(value) ? value : null
}

function compareSortValues(left, right, direction) {
  const difference = left - right
  return direction === 'ascending' ? difference : -difference
}

function pendingCourseExpirationDate(course) {
  if (course.duration_type !== constants.COURSES.DURATION_TYPE_DYNAMIC) {
    return DateManager.parseModelDate(course.available_until)
  }
  if (course.participation?.expires_at) {
    return DateManager.parseModelDate(course.participation.expires_at)
  }
  return calculateParticipationEnd(course, course.participation)
}

function pendingCoursePriority(course, nowTimestamp, recentThresholdTimestamp, upcomingThresholdTimestamp) {
  if ([true, false].includes(course.participation?.passed)) {
    return null
  }

  const participationUpdatedAt = nullableTimestamp(DateManager.parseModelDate(course.participation?.updated_at))
  const expiresAt = nullableTimestamp(pendingCourseExpirationDate(course))
  const createdAt = DateManager.parseModelDate(course.created_at).getTime()
  const hasUnfinishedParticipation = !!course.participation
  const isRecent = hasUnfinishedParticipation
    && participationUpdatedAt !== null
    && participationUpdatedAt >= recentThresholdTimestamp
    && participationUpdatedAt <= nowTimestamp
  const expiresSoon = expiresAt !== null
    && expiresAt >= nowTimestamp
    && expiresAt <= upcomingThresholdTimestamp

  if (isRecent) {
    return { group: 1, sortValue: participationUpdatedAt, sortDirection: 'descending' }
  }

  if (course.is_mandatory && expiresSoon) {
    return { group: 2, sortValue: expiresAt, sortDirection: 'ascending' }
  }

  if (!course.is_mandatory && hasUnfinishedParticipation && expiresSoon) {
    return { group: 3, sortValue: expiresAt, sortDirection: 'ascending' }
  }

  if (course.is_mandatory && hasUnfinishedParticipation
    && participationUpdatedAt !== null
    && participationUpdatedAt < recentThresholdTimestamp) {
    return { group: 4, sortValue: participationUpdatedAt, sortDirection: 'descending' }
  }

  if (course.is_mandatory && expiresAt !== null && expiresAt > upcomingThresholdTimestamp) {
    return { group: 5, sortValue: expiresAt, sortDirection: 'ascending' }
  }

  if (course.is_mandatory) {
    return { group: 6, sortValue: createdAt, sortDirection: 'descending' }
  }

  return null
}

export const prioritizePendingCourses = (courses, now = DateManager.now()) => {
  const nowTimestamp = now.getTime()
  const recentThresholdTimestamp = subDays(now, HOME_PENDING_COURSE_WINDOW_DAYS).getTime()
  const upcomingThresholdTimestamp = addDays(now, HOME_PENDING_COURSE_WINDOW_DAYS).getTime()

  return (courses || [])
    .map((course) => ({
      course,
      priority: pendingCoursePriority(
        course,
        nowTimestamp,
        recentThresholdTimestamp,
        upcomingThresholdTimestamp,
      ),
    }))
    .filter(({ priority }) => priority !== null)
    .sort((left, right) => {
      const groupComparison = left.priority.group - right.priority.group
      if (groupComparison !== 0) {
        return groupComparison
      }

      const sortValueComparison = compareSortValues(
        left.priority.sortValue,
        right.priority.sortValue,
        left.priority.sortDirection,
      )
      if (sortValueComparison !== 0) {
        return sortValueComparison
      }

      return right.course.id - left.course.id
    })
    .map(({ course }) => course)
}

const currentApiVersion = api.getAPIVersion()

export const compareVersion = (version) => {
  return currentApiVersion.localeCompare(version, undefined, {
    numeric: true,
  })
}
